import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography 
} from '@mui/material';

const REACT_APP_API_URL = 'https://gamecollection-app.onrender.com/api';

export default function AddConsoleModal({ open, onClose, onRefresh, platforms }) {
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  
  const initialForm = {
    platform_id: '',
    variation: '',
    serial_number: '',
    condition: '', // Free text
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  };

  const [form, setForm] = useState(initialForm);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.platform_id) return alert("Please select a platform");

    setSaving(true);
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined) {
        formData.append(k, v);
      }
    });
    
    if (uploadFile) {
      formData.append('boxart', uploadFile);
    }

    try {
      await axios.post(`${REACT_APP_API_URL}/console/add`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(initialForm);
      setUploadFile(null);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) {
      console.error("Hardware save error:", err);
      alert(`Failed to save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Add New Console</DialogTitle>
      <DialogContent dividers>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>Platform</InputLabel>
            <Select 
              value={form.platform_id} 
              label="Platform" 
              onChange={(e) => setForm({ ...form, platform_id: e.target.value })}
            >
              {platforms?.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField 
            label="Variation / Model" 
            fullWidth 
            value={form.variation} 
            onChange={(e) => setForm({ ...form, variation: e.target.value })} 
          />

          <TextField 
            label="Serial Number" 
            fullWidth 
            value={form.serial_number} 
            onChange={(e) => setForm({ ...form, serial_number: e.target.value })} 
          />

          {/* Condition as Free Text Field */}
          <TextField 
            label="Condition" 
            placeholder="e.g. CIB, Mint, Yellowed"
            fullWidth 
            value={form.condition} 
            onChange={(e) => setForm({ ...form, condition: e.target.value })} 
          />

          <TextField 
            label="Purchase Date" 
            type="date" 
            InputLabelProps={{ shrink: true }} 
            fullWidth 
            value={form.purchase_date} 
            onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} 
          />

          <TextField 
            label="Notes" 
            multiline 
            rows={3} 
            fullWidth 
            value={form.notes} 
            onChange={(e) => setForm({ ...form, notes: e.target.value })} 
          />

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" display="block">Console Photo (Optional)</Typography>
            <input type="file" accept="image/*" onChange={(e) => setUploadFile(e.target.files[0])} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={saving || !form.platform_id}
        >
          {saving ? <CircularProgress size={20} /> : "Save Console"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}